import { Badge, Button, Col, Row, Select, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { AiOutlineFileText, AiOutlineUser } from 'react-icons/ai';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Subscription } from 'rxjs';
import { useTheme } from 'styled-components';
import { ButtonType, TAButton } from '../../../components';
import { AcpcMark } from '../../../components/acpc-mark/acpc-mark';
import ActionsMenu from '../../../components/base/data-display/actions-menu';
import DataDisplay from '../../../components/base/data-display/data-display-selector';
import { CandidateAvatar } from '../../../components/dashboard/candidates/candidate-avatar';
import CandidateViewModal from '../../../components/dashboard/candidates/candidate-view-modal';
import JobOfferViewModal from '../../../components/dashboard/job-offer-view-modal';
import { SectionTitle, TASelect, TitleLink } from '../../../components/dashboard/styles';
import { PSUTMark } from '../../../components/psut-mark/psut-mark';
import { JobStatusMap } from '../../../constants/job-status';
import { useMedia, usePrevious } from '../../../hooks';
import authService from '../../../services/auth.service';
import companyService from '../../../services/company.service';
import jobsService from '../../../services/jobs.service';
import { CandidateType } from '../../../types/candidate-type';
import { JobOfferStatus } from '../../../types/offer';
import { getCandidateTypeFromCode } from '../../../utils';
import { appendQueryParam } from '../../../utils/append-query-param';
import { getConfig } from '../../../utils/config';
import { notifyError } from '../../../utils/notification';
import { objToSearchFilter } from '../../../utils/obj-to-search-filter';
import { objectToSearchParams } from '../../../utils/object-to-searchparams';
import { cleanUndefined } from '../../../utils/objects';
import { parseParams } from '../../../utils/parse-params';
import { verificationMapper } from '../../../utils/verification-mapper';
import classes from './offers-dashboard.module.css';

const badgeMapping = {
	Pending: 'processing',
	Withdrawn: 'warning',
	Accepted: 'success',
	Rejected: 'error'
};

const tenantLogoMapper = {
	[CandidateType.ACPC]: <AcpcMark />,
	[CandidateType.PSUT]: <PSUTMark />
};

const configService = getConfig();

const offersItemFactory =
	(viewCandidate, backOffice) =>
	({ item }) => {
		const theme = useTheme();
		return (
			<>
				<div>
					<CandidateAvatar
						showAvatar={backOffice}
						candidate={item.candidate}
						onClick={viewCandidate(item.candidate)}
					/>
				</div>

				<div className={classes.SecondColumn}>
					{backOffice && (
						<span
							onClick={viewCandidate(item.candidate)}
							color="#3F536E"
							className="py-2 d-inline-block"
							style={{ fontWeight: 500, cursor: 'pointer' }}
						>
							{`${item.candidate.firstName} ${item.candidate.lastName}`}
						</span>
					)}
					<span style={{ whiteSpace: 'break-spaces', width: '70%', textAlign: 'center' }}>
						<Link to={`/dashboard/job/${item.job.id}`} style={{ color: theme.TA_GREEN }}>
							{item.job.title}
						</Link>
						{backOffice && (
							<Link to={`/dashboard/company/${item.company.id}`} style={{ color: theme.TA_GREEN }}>
								@{item.company.name}
							</Link>
						)}
					</span>

					<Tooltip title={dayjs(item.createdAt).fromNow()}>
						{dayjs(item.createdAt).format('ddd, MMM D, YYYY h:mm A')}
					</Tooltip>

					<Badge status={badgeMapping[item.status]} text={item.status} />
				</div>
			</>
		);
	};
export const OffersDashboard: React.FC<any> = ({ backOffice }) => {
	const [jobs, setJobs] = useState<any>([]);
	const [offers, setOffers] = useState<any>([]);
	const [companies, setCompanies] = useState<any>([]);
	const [candidates, setCandidates] = useState<any>([]);
	const [display, setDisplay] = useState<any>([]);
	const [mobileDisplay, setMobileDisplay] = useState<any>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [showOfferView, setShowOfferView] = useState<boolean>(false);
	const [selectedCompany, setSelectedCompany] = useState<any>();
	const [selectedJob, setSelectedJob] = useState<any>();
	const [selectedOffer, setSelectedOffer] = useState<any>();
	const [allOffers, setAllOffers] = useState<boolean>(true);
	const [searchKeyword, setSearchKeyword] = useState<any>();
	const [selectedCandidate, setSelectedCandidate] = useState<any>();
	const [showCandidateModal, setShowCandidateModal] = useState<boolean>(false);
	const [recommendedCandidate, setRecommendedCandidate] = useState<any>({ jobId: null, job: null, company: null });
	const [pagination, setPagination] = useState<any>({});
	const [sortParams, setSortParams] = useState<any>({});
	const [companiesFilter, setCompaniesFilter] = useState<any>({});
	const [jobsFilter, setJobsFilter] = useState<any>({});
	const [offersFilter, setOffersFilter] = useState<any>({});
	const [displayCandidatesType, setDisplayCandidatesType] = useState<any>(CandidateType.TALENTS_ARENA);

	const theme = useTheme();

	const mediaView = useMedia('md');
	const location = useLocation();
	const prevLocation = usePrevious(location);
	const navigate = useNavigate();

	const tableColumns: any = [
		{
			title: (
				<span className="d-inline-block ps-3 py-2" style={{ color: '#8DABC4' }}>
					Candidate
				</span>
			),
			dataIndex: 'avatar',
			key: 'avatar',
			width: '10%',
			render: (candidate, record) => (
				<span className="px-1 d-flex justify-content-center">
					<CandidateAvatar
						link
						showAvatar={backOffice}
						candidate={record.candidate}
						onClick={viewCandidate(record.candidate)}
					/>
				</span>
			)
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Job
				</span>
			),
			dataIndex: 'job',
			key: 'job',
			width: '15%',
			render: (text, record) => (
				<Link to={`/dashboard/job/${record.job.id}`} style={{ color: theme.TA_GREEN }}>
					{text.title}
				</Link>
			)

			// sorter: (a, b) => (a.job.title.toLowerCase() > b.job.title.toLowerCase() ? 1 : -1)
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Offer Date
				</span>
			),
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: '17%',
			render: (text, record) => <span>{dayjs(new Date(text)).format('MMMM D, YYYY')}</span>,
			sorter: { multiple: 1 }

			// sorter: (a, b) => new Date(a.createdAt) > new Date(b.createdAt)
		},
		// {
		//     title: 'Last action Date',
		//     dataIndex: 'lastActionAt',
		//     key: 'lastActionAt',
		//     render: (text, record) => <span>{new Date(text).toLocaleDateString()}</span>,
		//     sorter: (a, b) => new Date(a.createdAt) > new Date(b.createdAt)

		// },
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Status
				</span>
			),
			dataIndex: 'status',
			key: 'status',
			width: '15%',
			render: (text, record) => <Badge status={badgeMapping[text]} text={text} />
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Actions
				</span>
			),
			dataIndex: 'actions',
			key: 'actios',
			width: '20%',
			render: (text, record) => <ActionsMenu dotsOrientation="horizontal" options={actionsFactory(record)} />
		}
	];

	if (backOffice) {
		tableColumns.splice(1, 0, {
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Name
				</span>
			),
			dataIndex: 'candidate',
			key: 'candidate',
			width: '17%',
			render: (candidate, record) => (
				<TitleLink
					color="#3F536E"
					to={`/dashboard/candidate/${candidate.id}/?source=${getCandidateTypeFromCode(candidate.code)}`}
					size={15}
					className="py-2 d-inline-block"
				>
					{tenantLogoMapper[getCandidateTypeFromCode(candidate.code)] ?? null}
				</TitleLink>
			)
			// <Link to={`/dashboard/candidates/list?candidate=${candidate._id}`}>
			// 	{`${candidate.firstName} ${candidate.lastName}`}
			// </Link>

			// sorter: (a, b) =>
			// `${a.firstName} ${a.lastName}`.toLowerCase() > `${b.firstName} ${b.lastName}`.toLowerCase() ? 1 : -1
		});
		tableColumns.splice(3, 0, {
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Company
				</span>
			),
			dataIndex: 'company',
			key: 'company',
			width: '15%',
			render: (text, record) => {
				const companyName = (
					<>
						<span className="mx-2">{record.company.name}</span>
						{verificationMapper(theme)[record.company.joinStatus]}
					</>
				);
				return (
					<Link to={`/dashboard/company/${record.company.id}`} style={{ color: theme.TA_GREEN }}>
						{companyName}
					</Link>
				);
			}
			// sorter: (a, b) => (a.company.name.toLowerCase() > b.company.name.toLowerCase() ? 1 : -1)
		});
	}

	const fetchOffers = params => {
		return jobsService.getOffers(null, params);
	};

	const fetchJobs = params => {
		return jobsService.getJobs(params);
	};

	const fetchCompanies = params => {
		return companyService.getCompanies(params);
	};

	const searchJobs$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: v =>
					fetchJobs({
						filter: v && v['filter'] ? objToSearchFilter(v['filter']) : null,
						pageSize: configService.defaultPageSize
					}),
				debounce: 700,
				distinct: true,
				beforeFetch: v => setJobsFilter(v && v['filter'] ? v['filter'] : {})
			}),
		[]
	);

	const searchCompanies$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: v =>
					fetchCompanies({
						filter: v && v['filter'] ? objToSearchFilter(v['filter']) : null,
						pageSize: configService.defaultPageSize
					}),
				debounce: 700,
				distinct: true,
				beforeFetch: v => setCompaniesFilter(v && v['filter'] ? v['filter'] : {})
			}),
		[]
	);

	const searchOffers$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: v => {
					return fetchOffers({
						filter: v && v['filter'] ? objToSearchFilter(cleanUndefined(v['filter'])) : null,
						pageSize: configService.defaultPageSize,
						sorters: sortParams
					});
				},
				debounce: 700,
				distinct: true,
				beforeFetch: v => setOffersFilter(v && v['filter'] ? cleanUndefined(v['filter']) : {})
			}),
		[]
	);

	const sortObs$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: ({ filter, sortInfo }) =>
					fetchOffers({
						sorters: sortInfo,
						pageSize: configService.defaultPageSize,
						filter
					}),
				debounce: 700,
				distinct: true,
				beforeFetch: ({ sortInfo }) => setSortParams(sortInfo ? { ...sortInfo } : undefined)
			}),
		[]
	);

	const changePage$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: fetchOffers,
				debounce: false,
				distinct: false,
				beforeFetch: v => setLoading(true)
			}),
		[]
	);

	const fetchQueryCompanyOrJob = async (filter, companies, jobs) => {
		if (backOffice) {
			if (filter.companyId) {
				let company = companies.find(company => company.id === filter.companyId);
				if (!company) {
					companies.push(
						(
							await companyService.getCompanies({
								filter: objToSearchFilter({ _id: filter.companyId })
							})
						).data[0]
					);
				}
			}
		}

		if (filter.jobId) {
			let job = jobs.find(job => job.id === filter.jobId);
			if (!job) {
				jobs.push(
					(
						await jobsService.getJobs(
							{
								filter: objToSearchFilter({ _id: filter.jobId })
							},
							null,
							true
						)
					).data[0]
				);
			}
		}
	};

	const subsribtions = new Subscription();
	const mountLogic = async () => {
		if (backOffice) {
			tableColumns.splice(1, 0, {
				title: (
					<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
						Name
					</span>
				),
				dataIndex: 'candidate',
				key: 'candidate',
				width: '17%',
				render: (candidate, record) => (
					<TitleLink
						color="#3F536E"
						to={`/dashboard/candidate/${candidate.id}/?source=${getCandidateTypeFromCode(candidate.code)}`}
						size={15}
						className="py-2 d-inline-block"
					>
						{`${candidate.firstName} ${candidate.lastName}`}{' '}
						{tenantLogoMapper[getCandidateTypeFromCode(candidate.code)] ?? null}
					</TitleLink>
				)
			});
			tableColumns.splice(3, 0, {
				title: (
					<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
						Company
					</span>
				),
				dataIndex: 'company',
				key: 'company',
				width: '15%',
				render: (text, record) => {
					const companyName = (
						<>
							<span className="mx-2">{record.company.name}</span>
							{verificationMapper(theme)[record.company.joinStatus]}
						</>
					);
					return (
						<Link to={`/dashboard/company/${record.company.id}`} style={{ color: theme.TA_GREEN }}>
							{companyName}
						</Link>
					);
				}
				// sorter: (a, b) => (a.company.name.toLowerCase() > b.company.name.toLowerCase() ? 1 : -1)
			});
		}
		if (backOffice) {
			subsribtions.add(
				searchCompanies$.subscribe(({ data: companies }) => {
					setLoading(false);
					setCompanies(companies);
				})
			);
		}

		subsribtions.add(
			sortObs$.subscribe(({ data, pagination }) => {
				setLoading(false);
				setOffers(data);
				setDisplay(data);
				setMobileDisplay([data]);
				setPagination(pagination);
			})
		);

		subsribtions.add(
			searchJobs$.subscribe(({ data: jobs }) => {
				setLoading(false);
				setJobs(jobs);
			})
		);

		subsribtions.add(
			searchOffers$.subscribe(({ data, pagination }) => {
				setLoading(false);
				setOffers(data);
				setDisplay(data);
				setMobileDisplay([data]);
				setPagination(pagination);
			})
		);
		subsribtions.add(
			changePage$.subscribe(({ data, pagination }) => {
				setLoading(false);
				setOffers(data);
				setDisplay(data);
				setMobileDisplay(prevState => [...prevState, data]);
				setPagination(pagination);
			})
		);

		let qs: any = {};
		if (location && location.search) {
			qs = parseParams(location.search);
			qs.page = +qs.page;
			qs.pageSize = +qs.pageSize;
		}
		if (!(qs.companyId || qs.jobId || qs.page))
			navigate({
				search: objectToSearchParams({
					page: 1,
					pageSize: configService.defaultPageSize,
					candidateType: qs.candidateType || CandidateType.TALENTS_ARENA
				})
			});

		let jobs,
			offers,
			companies,
			companiesPayload,
			selectedOffer,
			pagination,
			showOfferView = false;

		try {
			[{ data: jobs }, { data: offers, pagination }, companiesPayload] = await Promise.all([
				jobsService.getJobs(null, null, true),
				jobsService.getOffers(null, {
					page: qs.page ? qs.page : 1,
					pageSize: qs.pageSize ? qs.pageSize : configService.defaultPageSize,
					filter: objToSearchFilter({
						candidateType: qs.candidateType || CandidateType.TALENTS_ARENA
					})
				}),
				backOffice ? companyService.getCompanies() : Promise.resolve(undefined)
			]);

			companies = companiesPayload?.data;
			fetchQueryCompanyOrJob(qs, companies, jobs);

			//handle if no jobs are sent.
			if (jobs === null || (backOffice && companies === null)) throw new Error();
		} catch (e) {
			//handle promise error.
			notifyError('Error while fetching data, Please try again later.');
			setLoading(false);
			return;
		}
		offers = offers.map(o => {
			o.candidate.avatarBgColor = '#4ADCAA';
			return o;
		});

		setLoading(false);
		setJobs(jobs);
		setOffers(offers);
		setDisplay(offers);
		setMobileDisplay([offers]);
		setPagination(pagination);
		setDisplayCandidatesType(qs.candidateType || CandidateType.TALENTS_ARENA);
		setCompanies(backOffice ? companies : []);

		if (location && location.search) {
			const queryParams = {};
			if (backOffice && qs.companyId) {
				queryParams['companyId'] = qs.companyId;
				handleCompanySelect(qs.companyId);
			}
			if (qs.jobId) {
				queryParams['jobId'] = qs.jobId;
				handleJobSelect(qs.jobId);
			}
			if (qs.candidateType) {
				queryParams['candidateType'] = qs.candidateType;
				setDisplayCandidatesType(qs.candidateType);
			}

			if (Object.keys(queryParams).length) searchOffers$.next({ filter: queryParams });

			if (qs.offer) viewCandidateOffer(qs.offer)();
		}
	};
	useEffect(() => {
		mountLogic();
		return () => subsribtions.unsubscribe();
	}, []);

	const locationSearchChangeLogic = () => {
		const newSearch = location?.search;
		const oldSearch = prevLocation?.search;
		if (!newSearch) {
			changePage$.next();
			setJobsFilter({});
			setCompaniesFilter({});
			setSelectedCompany(null);
			setSelectedJob(null);
		} else if (newSearch && oldSearch && newSearch !== oldSearch) {
			const newParams = parseParams(newSearch);
			const oldParams = parseParams(oldSearch);

			const newFilter: any = cleanUndefined({
				companyId: newParams.companyId,
				jobId: newParams.jobId,
				candidateType: newParams.candidateType
			});
			const oldFilter: any = cleanUndefined({
				companyId: oldParams.companyId,
				jobId: oldParams.jobId,
				candidateType: oldParams.candidateType
			});

			if (!newFilter.jobId) {
				setJobsFilter({});
				setSelectedJob(null);
			}

			if (!newFilter.companyId) {
				setCompaniesFilter({});
				setSelectedCompany(null);
				setOffersFilter({ ...offersFilter, companyId: undefined });
			}

			if (Object.keys(newFilter).length) {
				if (
					newFilter.companyId !== oldFilter.companyId ||
					newFilter.jobId !== oldFilter.jobId ||
					newFilter.candidateType !== oldFilter.candidateType
				) {
					fetchQueryCompanyOrJob(newFilter, companies, jobs);
					setOffersFilter(newFilter);
					setSelectedCompany(newFilter.companyId);
					setSelectedJob(newFilter.jobId);
					searchOffers$.next({ filter: newFilter });
				}
			}
			if (
				(newParams.page && newParams.page !== oldParams.page) ||
				(newParams.pageSize && newParams.pageSize !== oldParams.pageSize)
			) {
				if (!mediaView) searchOffers$.next({ filter: newFilter });
				else
					changePage$.next({
						page: newParams.page,
						pageSize: newParams.pageSize,
						filter: objToSearchFilter(newFilter),
						sorters: sortParams
					});
			}
		}
	};

	useEffect(() => {
		if (location?.search) locationSearchChangeLogic();
	}, [location?.search]);

	const viewCandidate = candidate => () => {
		if (!offers) return;
		if (candidate) {
			setSelectedCandidate(candidate);
			setShowCandidateModal(true);
		}
	};

	const viewCandidateOffer = offerId => () => {
		if (!offers) return;
		appendQueryParam(location.search, navigate, offerId && [{ offer: offerId, ...offersFilter }]);
		const offer = (offers || offers).find(offer => offer.id === offerId);
		if (offer) {
			setSelectedOffer(offer);
			setShowOfferView(true);
		}
	};

	const changeOfferState = state => () => {
		const updatedOffers = offers.map(offer => {
			if (offer.id === selectedOffer.id) {
				offer.status = state;
			}
			return offer;
		});

		const updatedMobileDisplay = mobileDisplay.map(offers => {
			return offers.map(offer => {
				if (offer.id === selectedOffer.id) {
					offer.status = state;
				}
				return offer;
			});
		});

		setShowOfferView(false);
		setSelectedOffer(null);
		setOffers(offers);
		setDisplay(updatedOffers);
		setMobileDisplay(updatedMobileDisplay);
	};

	const onCounterSuccess = counterOffer => {
		let newSelected;
		const updatedOffers = offers.map(offer => {
			if (offer.id === selectedOffer.id) {
				newSelected = offer = { ...offer, counterOffers: [...offer.counterOffers, counterOffer] };
			}
			return offer;
		});

		const updatedMobileDisplay = mobileDisplay.map(offers => {
			return offers.map(offer => {
				if (offer.id === selectedOffer.id) {
					newSelected = offer = { ...offer, counterOffers: [...offer.counterOffers, counterOffer] };
				}
				return offer;
			});
		});

		setOffers(updatedOffers);
		setDisplay(updatedOffers);
		setSelectedOffer(newSelected);
		setMobileDisplay(updatedMobileDisplay);
	};

	const hideOfferViewModal = () => {
		const filter: any = parseParams(location.search);
		delete filter.offer;
		navigate({ search: objectToSearchParams(filter) });
		setShowOfferView(false);
		setSelectedOffer(null);
	};

	const handleCompanySearch = value => {
		searchCompanies$.next({
			filter: { name: value }
		});
	};
	const handleJobSearch = value => {
		searchJobs$.next({
			filter: { title: value, companyId: selectedCompany }
		});
	};

	const handleJobSelect = val => {
		const filter = { ...offersFilter };
		filter['jobId'] = val;

		navigate(objectToSearchParams(filter));

		searchOffers$.next({ filter });
		setSelectedJob(val);
		setLoading(false);
	};

	const handleCompanySelect = val => {
		let filter = { ...offersFilter };
		filter['companyId'] = val;
		if (!filter['companyId']) {
			filter = {};
		}
		if (filter['jobId']) delete filter['jobId'];
		navigate(objectToSearchParams({ ...filter, candidateType: displayCandidatesType }));
		searchJobs$.next({ filter });
		searchOffers$.next({ filter: { ...filter, candidateType: displayCandidatesType } });
		setSelectedCompany(val);
		setSelectedJob(null);
		setLoading(false);
	};

	const handleSort = sortInfo => {
		sortObs$.next({
			sortInfo,
			filter: objToSearchFilter({
				...jobsFilter,
				...companiesFilter,
				...offersFilter
			})
		});
	};

	const hideCandidateViewModal = () => {
		setShowCandidateModal(false);
		setSelectedCandidate(null);
	};

	const displayOffers = (all = true) => {
		setAllOffers(all);
		setDisplay((all && offers) || offers.filter(offer => offer.createdBy === authService.getDecodedToken().sub));
	};

	const displayAllOffers = () => displayOffers(true);

	const displayMyOffers = () => displayOffers(false);

	const handlePageChange = (page, pageSize) => {
		if (mediaView) navigate({ search: objectToSearchParams({ page, pageSize, ...offersFilter }) });
		else
			changePage$.next({
				page,
				pageSize,
				filter: objToSearchFilter({
					...jobsFilter,
					...companiesFilter,
					...offersFilter
				}),
				sorters: sortParams
			});
	};
	const handleDisplayedCandidates = type => {
		setDisplayCandidatesType(type);
		navigate(objectToSearchParams({ ...offersFilter, candidateType: type }));
		searchOffers$.next({ filter: { ...offersFilter, candidateType: type } });
	};

	const actionsFactory = item => {
		const actions = [
			{
				name: 'View Candidate',
				key: 'viewCandidate',
				action: viewCandidate(item.candidate),
				icon: <AiOutlineUser />
			},
			{
				name: 'View Offer',
				key: 'viewOffer',
				action: viewCandidateOffer(item.id),
				icon: <AiOutlineFileText />
			}
		];
		return actions;
	};

	return (
		<div className={`dashboard`} style={{ width: '95%' }}>
			<Row gutter={24} className="mb-3">
				<Col xs={{ span: 24 }} className="text-start page-label">
					<SectionTitle>Offers</SectionTitle>
				</Col>
				<Col
					xs={{ span: 24 }}
					className="text-end d-flex align-items-stretch justify-content-lg-start flex-wrap"
				>
					{backOffice ? (
						<Fragment>
							<Col xs={24} lg={4} className="text-start d-flex align-items-end mb-3 mb-lg-0">
								You have{' '}
								<span
									style={{
										color: theme.TA_GREEN,
										borderBottom: `2px dashed ${theme.TA_GREEN}`,
										fontSize: '16px',
										marginLeft: '5px'
									}}
								>
									{pagination.total} offers
								</span>
							</Col>
							<Col
								xs={24}
								lg={{ span: 10 }}
								className="d-flex justify-content-center justify-content-lg-end"
							>
								<Button.Group style={{ padding: '4px 0px', height: 'auto' }}>
									<TAButton
										btntype={
											displayCandidatesType === CandidateType.TALENTS_ARENA
												? ButtonType.PRIMARY
												: ButtonType.GHOST
										}
										onClick={() => handleDisplayedCandidates(CandidateType.TALENTS_ARENA)}
									>
										Talents Arena
									</TAButton>
									<TAButton
										btntype={
											displayCandidatesType === CandidateType.PSUT
												? ButtonType.PRIMARY
												: ButtonType.GHOST
										}
										onClick={() => handleDisplayedCandidates(CandidateType.PSUT)}
									>
										PSUT
									</TAButton>
								</Button.Group>
							</Col>
							<Col xs={24} lg={{ span: 5 }}>
								<TASelect
									value={selectedCompany || undefined}
									onChange={handleCompanySelect}
									filterOption={(input, option) =>
										option.props.children[0].props.children
											.toLowerCase()
											.indexOf(input.toLowerCase()) >= 0
									}
									placeholder="Select a company"
									onSearch={handleCompanySearch}
									className="mb-3 mb-lg-0"
									showSearch
								>
									{companies.map(c => {
										console.log(verificationMapper(theme)[c.joinStatus]);
										return (
											<Select.Option value={c.id} key={c.id}>
												<span className="mx-2">{c.name}</span>
												{verificationMapper(theme)[c.joinStatus]}
											</Select.Option>
										);
									})}
								</TASelect>
							</Col>
							<Col xs={24} lg={{ span: 5 }}>
								<TASelect
									filterOption={(input, option) =>
										option.props.children[0].props.children
											.toLowerCase()
											.indexOf(input.toLowerCase()) >= 0
									}
									value={selectedJob || undefined}
									onChange={handleJobSelect}
									placeholder="Select a job"
									style={{ width: '100%' }}
									className="ta-ant-input"
									showSearch
									onSearch={handleJobSearch}
								>
									{jobs &&
										jobs
											.filter(job => (selectedCompany ? job.company.id == selectedCompany : true))
											.map(job => (
												<Select.Option value={job._id} key={job._id}>
													<span className="me-2">{`${job.title} | ${job.company.name}`}</span>
													<Tag color={JobStatusMap[job.status]}>{job.status}</Tag>
												</Select.Option>
											))}
								</TASelect>
							</Col>
						</Fragment>
					) : (
						<Fragment>
							<Col xs={24} lg={4} className="text-start d-flex align-items-end mb-3 mb-lg-0">
								You have{' '}
								<span
									style={{
										color: theme.TA_GREEN,
										borderBottom: `2px dashed ${theme.TA_GREEN}`,
										fontSize: '16px',
										marginLeft: '5px'
									}}
								>
									{pagination.total} offers
								</span>
							</Col>
							<Col xs={24} lg={10} className="d-flex justify-content-center justify-content-lg-end">
								<Button.Group style={{ padding: '4px 0px', height: 'auto' }}>
									<TAButton
										btntype={
											displayCandidatesType === CandidateType.TALENTS_ARENA
												? ButtonType.PRIMARY
												: ButtonType.GHOST
										}
										onClick={() => handleDisplayedCandidates(CandidateType.TALENTS_ARENA)}
									>
										Talents Arena
									</TAButton>
									<TAButton
										btntype={
											displayCandidatesType === CandidateType.PSUT
												? ButtonType.PRIMARY
												: ButtonType.GHOST
										}
										onClick={() => handleDisplayedCandidates(CandidateType.PSUT)}
									>
										PSUT
									</TAButton>
								</Button.Group>
							</Col>
							<Col xs={24} lg={{ span: 5, push: 5 }}>
								<TASelect
									value={selectedJob}
									onChange={handleJobSelect}
									placeholder="Select a job"
									className="mb-2 mb-lg-0"
									optionFilterProp="children"
									filterOption={(input, option) =>
										option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
									}
									showSearch
								>
									{jobs.map(job => (
										<Select.Option value={job.id} key={job.id}>
											{job.title}
										</Select.Option>
									))}
								</TASelect>
							</Col>
						</Fragment>
					)}
				</Col>
			</Row>
			<div className="dashboard-content">
				<DataDisplay
					className="ta-table"
					data={display}
					mobileData={mobileDisplay}
					loading={loading}
					tableColumns={tableColumns}
					listItemComponent={offersItemFactory(viewCandidate, backOffice)}
					renderActions={actionsFactory}
					size="small"
					pagination={{ position: 'bottom', ...pagination }}
					onLoadMore={handlePageChange}
					onSort={handleSort}
				/>
				{selectedCandidate && showCandidateModal ? (
					<CandidateViewModal
						visible={showCandidateModal}
						candidate={selectedCandidate}
						onCancel={hideCandidateViewModal}
						backOffice={backOffice}
						minimal={!backOffice}
					/>
				) : null}
				{selectedOffer ? (
					<JobOfferViewModal
						visible={showOfferView}
						offer={selectedOffer}
						job={selectedOffer.job}
						company={selectedOffer.company}
						onCancel={hideOfferViewModal}
						onWithdraw={changeOfferState(JobOfferStatus.WITHDRAWN)}
						onAccept={changeOfferState(JobOfferStatus.ACCEPTED)}
						onReject={changeOfferState(JobOfferStatus.REJECTED)}
						onCounter={onCounterSuccess}
						backOffice={backOffice}
					/>
				) : null}
			</div>
		</div>
	);
};
